import { useMemo, useState } from 'react';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Input } from '@/components/ui/Input';
import { Table, type Column } from '@/components/ui/Table';
import type { AuditLogEntry } from '@/api/types';

const RECENT_LIMIT = 200;

/** Who acted: name with email underneath; a bare id only when the account no longer exists. */
function Actor({ entry }: { entry: AuditLogEntry }) {
  if (!entry.actorEmail) {
    return entry.actorId ? <span className="text-fg-muted" title={entry.actorId}>Izbrisan uporabnik</span> : <span>Sistem</span>;
  }
  return (
    <div className="flex flex-col">
      <span className="text-fg">{entry.actorName || entry.actorEmail}</span>
      {entry.actorName && <span className="text-xs text-fg-muted">{entry.actorEmail}</span>}
    </div>
  );
}

export function AdminLogsPage() {
  const [search, setSearch] = useState('');
  const logs = useAuditLogs(RECENT_LIMIT);

  const filtered = useMemo(() => {
    const items = logs.data ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((l) =>
      [l.action, l.entityType, l.entityId, l.actorName, l.actorEmail, l.actorId, l.details]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(term)),
    );
  }, [logs.data, search]);

  const columns: Column<AuditLogEntry>[] = [
    { header: 'Čas', accessor: (l) => new Date(l.timestamp).toLocaleString() },
    { header: 'Uporabnik', accessor: (l) => <Actor entry={l} /> },
    { header: 'Dejanje', accessor: (l) => l.action },
    { header: 'Entiteta', accessor: (l) => (l.entityType ? `${l.entityType}${l.entityId ? ` #${l.entityId}` : ''}` : '—') },
    { header: 'Podrobnosti', accessor: (l) => l.details ?? '—' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-fg">Dnevnik revizije</h1>
      <p className="text-sm text-fg-muted">Prikazanih je {RECENT_LIMIT} najnovejših vnosov na celotni platformi.</p>

      <div className="max-w-sm">
        <Input placeholder="Filtriraj dnevnik" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {logs.isLoading && <PageSpinner />}
      {logs.isError && <ErrorState message={getApiErrorMessage(logs.error)} onRetry={() => logs.refetch()} />}
      {logs.isSuccess && filtered.length === 0 && <EmptyState title="Ni najdenih vnosov v dnevniku" />}
      {logs.isSuccess && filtered.length > 0 && <Table columns={columns} rows={filtered} getRowKey={(l) => l.id} />}
    </div>
  );
}
